import { stockService } from '@/services/stock';
import { cartService } from '@/services/cart';
import type { CartItem, OrderType } from './cart.types';

/**
 * DB에서 현재 장바구니를 가져와 로컬 상태에 동기화
 */
export const syncFromDB = async (
  userId: string,
  setState: (updates: any) => void
) => {
  try {
    console.log('[Cart Store] Syncing cart from DB for user:', userId);
    const dbCart = await cartService.getCurrentCart(userId);
    
    if (!dbCart || !dbCart.items || dbCart.items.length === 0) {
      console.log('[Cart Store] No active cart found in DB, clearing local state');
      // No cart in DB, clear local state
      setState({
        storeId: null,
        storeName: null,
        orderType: null,
        items: [],
        pickupTime: null,
        desiredPickupTime: null,
        pickupDate: null,
        pickupMethod: 'PICKUP',
        usedPoints: 0,
      });
      return;
    }
    
    console.log('[Cart Store] Found cart in DB with', dbCart.items.length, 'items');
    
    // Determine order type from first item's offer
    const firstOffer = dbCart.items[0]?.offer;
    const orderType = firstOffer?.offer_type || null;
    
    // Convert DB cart items to local cart items (재고는 즉시 세팅 시도)
    const today = new Date().toISOString().split('T')[0];
    const localItems: CartItem[] = dbCart.items.map(item => {
      const stockFromJoin = item.product?.stock?.find((s: any) => s.date === today)?.current_stock;
      return {
        productId: item.product_id,
        productName: item.product?.name || 'Unknown Product',
        productImage: item.product?.image_url,
        originalPrice: item.offer?.discounted_price || item.unit_price, // Fallback to unit_price
        discountedPrice: item.unit_price,
        discountRate: item.offer?.discount_rate || 0,
        quantity: item.quantity,
        currentStock: typeof stockFromJoin === 'number' ? stockFromJoin : 5, // 기본값 5
        options: undefined
      } as CartItem;
    });
    
    // Update stock information for each item
    for (const item of localItems) {
      try {
        // 이미 조인된 재고가 있으면 스킵
        if (typeof item.currentStock === 'number' && item.currentStock !== 5) continue;

        const stockData = await stockService.getProductStock(item.productId, today, false);
        if (stockData) {
          item.currentStock = stockData.current_stock;
        } else {
          item.currentStock = 5; // 조회 실패 시 안전값
        }
      } catch (error) {
        console.error('[Cart Store] Failed to get stock for product:', item.productId, error);
        item.currentStock = 5; // 오류 시 안전값
      }
    }
    
    // Update local state with DB data
    setState({
      storeId: dbCart.restaurant_id,
      storeName: dbCart.restaurant?.name || 'Unknown Store',
      orderType: orderType as OrderType,
      items: localItems,
      pickupTime: null,
      desiredPickupTime: null,
      pickupDate: null,
      pickupMethod: 'PICKUP',
      usedPoints: 0,
    });
    
    console.log('[Cart Store] Cart synced from DB successfully');
  } catch (error) {
    console.error('[Cart Store] Error syncing cart from DB:', error);
    // Don't clear local state on error, keep whatever is in localStorage
  }
};

/**
 * 장바구니 재고 검증
 */
export const validateCartStock = async (items: CartItem[]) => {
  if (items.length === 0) {
    return { isValid: true, errors: [] };
  }
  
  try {
    const validation = await stockService.validateCartStock(
      items.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        productName: item.productName
      }))
    );
    
    return {
      isValid: validation.isValid,
      errors: validation.errors
    };
  } catch (error) {
    console.error('Error validating cart stock:', error);
    return {
      isValid: false,
      errors: ['재고 확인 중 오류가 발생했습니다']
    };
  }
};

/**
 * 매장 변경을 위한 장바구니 초기화
 */
export const clearCartForStoreChange = async (userId?: string) => {
  if (userId) {
    await cartService.clearCartForRestaurantChange(userId);
  }
};

/**
 * 상품의 할인 오퍼 ID 찾기
 */
export const findOfferByType = async (productId: string, orderType: OrderType) => {
  return await cartService.findOfferByType(productId, orderType);
};

/**
 * DB에 상품 추가
 */
export const addToCartDB = async (productId: string, offerId: string, quantity: number, userId: string) => {
  return await cartService.addToCart(productId, offerId, quantity, userId);
};

/**
 * DB에서 상품 수량 업데이트
 */
export const updateCartItemDB = async (cartItemId: string, quantity: number) => {
  return await cartService.updateCartItemQuantity(cartItemId, quantity);
};

/**
 * DB에서 상품 제거
 */
export const removeFromCartDB = async (cartItemId: string) => {
  return await cartService.removeFromCart(cartItemId);
};

/**
 * DB에서 장바구니 완전 초기화
 */
export const clearCartDB = async (cartId: string) => {
  return await cartService.clearCart(cartId);
};
